import { useCallback, useState } from 'react';
import { useMutation } from '@apollo/client';
import {
  IEntityCategoryCreateMutation,
  IEntityCategoryCreateMutationVariables,
  IEntityCategoryFieldsFragment,
} from './apollo/graphqlSchema';
import { CREATE_ENTITY_CATEGORY } from './apollo/entityCategories';
import { assertNotNil, ICreatableReactSelectOption } from './types';
import { defaultManagedOption } from '../components/Categories/utils';

export const useSaveCategories = (
  schemaId: string,
  entityCategories?: IEntityCategoryFieldsFragment[],
  isManaged?: boolean,
) => {
  const [isEntityManaged, setIsEntityManaged] = useState(isManaged ?? false);
  const [categories, setCategories] = useState<ICreatableReactSelectOption[]>(
    () => {
      if (entityCategories && entityCategories.length > 0) {
        return entityCategories.map((entityCategory) => ({
          label: entityCategory.name,
          value: entityCategory.id,
        }));
      }

      return [];
    },
  );

  const [createEntityCategory] = useMutation<
    IEntityCategoryCreateMutation,
    IEntityCategoryCreateMutationVariables
  >(CREATE_ENTITY_CATEGORY);

  const prepareCategories = useCallback(async () => {
    if (!categories.length) {
      return [];
    }

    const categoriesToCreate = categories.filter(
      (category) => category.value !== defaultManagedOption.value,
    );

    // state categories can store both old and new(created from UI) categories.
    // In order to proceed with mutations, first we need to create category for each
    // new value. Once created we will push an id to result categories list
    const { newCategories, resultCategories } = categoriesToCreate.reduce<{
      newCategories: string[];
      resultCategories: string[];
    }>(
      (acc, category) => {
        if (category.__isNew__) {
          acc.newCategories.push(category.value);
        } else {
          acc.resultCategories.push(category.value);
        }

        return acc;
      },
      {
        newCategories: [],
        resultCategories: [],
      },
    );

    if (newCategories.length > 0) {
      const createdResults = await Promise.all(
        newCategories.map((newCategory) =>
          createEntityCategory({
            variables: {
              input: {
                color: null,
                name: newCategory,
                schema: schemaId,
              },
            },
          }),
        ),
      );

      createdResults.forEach((result) => {
        const id = result?.data?.entityCategoryCreate?.entity?.id;
        assertNotNil(id);
        resultCategories.push(id);
      });
    }

    return resultCategories;
  }, [categories, createEntityCategory, schemaId]);

  return {
    categories,
    setCategories,
    prepareCategories,
    isEntityManaged,
    setIsEntityManaged,
  };
};
